import { OPERATOR_FLOW, OPERATOR_FLOW_PATHS } from '../operator/operatorConstants';
import { orderCardTitle } from '../operator/operatorUtils';
import { formatQuantity } from '../../utils/inventoryLabels';

/*
 * The tray's rules on this side: where each task opens, and how its time,
 * amount and reason read on the floor. Times are measured against the
 * server's clock (`selectTasksNow`), so a phone whose clock is off still says
 * how long a task has really waited.
 */

/** The task types the front knows how to open; the tray is asked for these only. */
export const TASK_TYPE = {
  PACK: 'pack',
  PICK: 'pick',
  RECEIVE: 'receive',
  PUTAWAY: 'putaway',
  RESTOCK: 'restock',
  /** A courier's orders ready to leave: `entity` is `{kind: 'courier', code, label}`. */
  SHIP: 'ship',
};

export const TASK_TYPE_LABELS = {
  [TASK_TYPE.PICK]: 'Picking',
  [TASK_TYPE.PACK]: 'Empaque',
  [TASK_TYPE.RECEIVE]: 'Recepción',
  [TASK_TYPE.PUTAWAY]: 'Almacenado',
  [TASK_TYPE.RESTOCK]: 'Reposición',
  [TASK_TYPE.SHIP]: 'Despacho',
};

export const TASK_TYPES = Object.values(TASK_TYPE);

/**
 * The types to ask the tray of a warehouse for. `ship` only where its flow is
 * open: a server that does not know the type yet answers the whole tray 400.
 *
 * @param {{flows?: object}|null} warehouse
 */
export const taskTypesFor = (warehouse) =>
  TASK_TYPES.filter((type) => type !== TASK_TYPE.SHIP || warehouse?.flows?.[OPERATOR_FLOW.DISPATCH] === true);

/** How many tasks of the rest the tray brings. */
export const TASKS_LIMIT = 30;

/** How often the local clock moves the times on screen; a list sitting still is asked again on its tick. */
export const TASKS_CLOCK_MS = 60 * 1000;

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
// As the server says them: above two days a span reads in days, not hours.
const DAYS_FROM = 48 * HOUR;
const WAITING_LONG = 24 * HOUR;
const DUE_CRITICAL = HOUR;
const DUE_RISK = 4 * HOUR;

/** The reasons that are a time, which the time chip says live. */
const TIME_REASONS = ['waiting', 'overdue', 'due_within_hour', 'due_within_4_hours'];
const REASON_SEPARATOR = ' · ';

const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;

/**
 * A server time in milliseconds, or `null`. DRF sends microseconds, which
 * not every browser parses: the fraction is cut to milliseconds first.
 *
 * @param {string|null|undefined} value
 */
export const parseServerTime = (value) => {
  if (!value) return null;
  const time = Date.parse(String(value).replace(/(\.\d{3})\d+/, '$1'));
  return Number.isNaN(time) ? null : time;
};

/**
 * A stretch of time as the floor says it: minutes, hours, or days past two.
 * With `withMinutes`, the hours carry their minutes: «1 h 20 min».
 *
 * @param {number} ms
 * @param {{withMinutes?: boolean}} [options]
 */
export const spanText = (ms, { withMinutes = false } = {}) => {
  const minutes = Math.floor(Math.max(ms, 0) / MINUTE);
  if (minutes < 60) return `${Math.max(minutes, 1)} min`;
  if (ms >= DAYS_FROM) return `${Math.floor(ms / DAY)} días`;
  const rest = minutes % 60;
  return withMinutes && rest ? `${Math.floor(minutes / 60)} h ${rest} min` : `${Math.floor(minutes / 60)} h`;
};

/**
 * «Esperando hace 3 h», or `null` without a start or a clock.
 *
 * @param {string|null} waitingSince
 * @param {number|null} now - The server's time.
 */
export const ageText = (waitingSince, now) => {
  const since = parseServerTime(waitingSince);
  if (since === null || now == null) return null;
  return `Esperando hace ${spanText(now - since)}`;
};

/**
 * «Vence en 1 h 20 min» or «Vencida hace 15 min», or `null` without a deadline.
 *
 * @param {string|null} dueAt
 * @param {number|null} now - The server's time.
 */
export const dueText = (dueAt, now) => {
  const due = parseServerTime(dueAt);
  if (due === null || now == null) return null;
  const left = due - now;
  return left < 0
    ? `Vencida hace ${spanText(-left, { withMinutes: true })}`
    : `Vence en ${spanText(left, { withMinutes: true })}`;
};

/**
 * How close a deadline is, by the same thresholds the server ranks with:
 * `overdue`, `critical` (an hour or less), `risk` (four hours or less), `ok`;
 * `none` without one.
 */
export const urgencyAt = (dueAt, now) => {
  const due = parseServerTime(dueAt);
  if (due === null || now == null) return 'none';
  const left = due - now;
  if (left < 0) return 'overdue';
  if (left <= DUE_CRITICAL) return 'critical';
  if (left <= DUE_RISK) return 'risk';
  return 'ok';
};

const DUE_TONES = { overdue: 'danger', critical: 'danger', risk: 'warning', ok: 'neutral' };

/**
 * What a task's time chip says: its deadline when it has one, otherwise how
 * long it has waited; and its tone. `null` when there is nothing to say.
 *
 * @param {{due_at?: string|null, waiting_since?: string|null}} task - A tray task or a queue row.
 * @param {number|null} now - The server's time.
 * @returns {{kind: 'due'|'age', text: string, tone: string}|null}
 */
export const taskTimeOf = (task, now) => {
  const due = dueText(task?.due_at, now);
  if (due) return { kind: 'due', text: due, tone: DUE_TONES[urgencyAt(task.due_at, now)] };
  const age = ageText(task?.waiting_since, now);
  if (!age) return null;
  const long = now - parseServerTime(task.waiting_since) > WAITING_LONG;
  return { kind: 'age', text: age, tone: long ? 'warning' : 'neutral' };
};

/**
 * Where a task opens, or `null` for a type this front does not know. A
 * courier's dispatch opens the station filtered by it.
 *
 * @param {{type: string, entity: {id?: number, row_key?: string, code?: string}}} task
 */
export const taskPath = (task) => {
  const entity = task?.entity;
  if (!entity) return null;
  switch (task.type) {
    case TASK_TYPE.PICK:
      return `${OPERATOR_FLOW_PATHS[OPERATOR_FLOW.PICKING]}/${entity.id}`;
    case TASK_TYPE.PACK:
      return `${OPERATOR_FLOW_PATHS[OPERATOR_FLOW.PACKING]}/${entity.id}`;
    case TASK_TYPE.RECEIVE:
      return `${OPERATOR_FLOW_PATHS[OPERATOR_FLOW.RECEIVING]}/${entity.id}`;
    case TASK_TYPE.PUTAWAY:
    case TASK_TYPE.RESTOCK: {
      const source = task.type === TASK_TYPE.PUTAWAY ? 'receiving' : 'restock';
      return `${OPERATOR_FLOW_PATHS[OPERATOR_FLOW.PUTAWAY]}?source=${source}&key=${encodeURIComponent(entity.row_key)}`;
    }
    case TASK_TYPE.SHIP: {
      const path = OPERATOR_FLOW_PATHS[OPERATOR_FLOW.DISPATCH];
      return entity.code ? `${path}?courier=${encodeURIComponent(entity.code)}` : path;
    }
    default:
      return null;
  }
};

/**
 * What a task's card reads: an order by its number and reference, «#52 -
 * ORD-52»; a receipt, a dispatch or a putaway by the reference the server
 * gives it. Not the order's `reference`: without one it is «#52» already.
 *
 * @param {{reference: string, entity?: {kind: string, id?: number, reference_id?: string|null}}} task
 */
export const taskTitle = (task) => (task?.entity?.kind === 'order' ? orderCardTitle(task.entity) : task?.reference);

const AMOUNT_VERBS = {
  [TASK_TYPE.PICK]: 'por pickear',
  [TASK_TYPE.PACK]: 'por empacar',
  [TASK_TYPE.RECEIVE]: 'por recibir',
  [TASK_TYPE.PUTAWAY]: 'por guardar',
  [TASK_TYPE.RESTOCK]: 'por guardar',
};

/**
 * How much work a task is: «3 un por pickear · 2 líneas»; a dispatch counts
 * items and orders, «8 bultos · 5 órdenes». `null` for an order with nothing
 * to count, as one without lines is.
 *
 * @param {{type: string, units: number, lines: number}} task
 */
export const taskQuantityText = (task) => {
  const units = Number(task?.units) || 0;
  const lines = Number(task?.lines) || 0;
  if (units <= 0 && lines <= 0) return null;
  if (task.type === TASK_TYPE.SHIP) return `${plural(units, 'bulto', 'bultos')} · ${plural(lines, 'orden', 'órdenes')}`;
  const amount = [`${formatQuantity(units)} un`, AMOUNT_VERBS[task.type]].filter(Boolean).join(' ');
  const oneRow = [TASK_TYPE.PUTAWAY, TASK_TYPE.RESTOCK].includes(task.type);
  return oneRow ? amount : `${amount} · ${plural(lines, 'línea', 'líneas')}`;
};

/**
 * Why a task goes where it goes, without the reasons that are a time: the
 * time chip says those as they move. `null` when nothing else is left.
 *
 * @param {{reason?: string, reason_codes?: string[]}} task
 */
export const taskReasonText = (task) => {
  const reason = String(task?.reason || '');
  const codes = task?.reason_codes || [];
  const parts = reason.split(REASON_SEPARATOR);
  if (!reason || parts.length !== codes.length) return reason || null;
  return parts.filter((_, index) => !TIME_REASONS.includes(codes[index])).join(REASON_SEPARATOR) || null;
};

/**
 * Who is on a task: «En curso por Juan», «En curso por ti», or just «En
 * curso» where nobody is recorded (packing). `null` when nobody is.
 *
 * @param {{in_progress?: boolean, activity?: {by_me: boolean, by_name: string|null}|null}} task
 */
export const activityText = (task) => {
  if (!task?.in_progress) return null;
  if (task.activity?.by_me) return 'En curso por ti';
  if (task.activity?.by_name) return `En curso por ${task.activity.by_name}`;
  return 'En curso';
};

/** Whether a task is on someone else's hands. */
export const takenByAnother = (task) => Boolean(task?.in_progress && task.activity && !task.activity.by_me);

/** The verb of the button that opens a task. */
export const startLabel = (task) => (task?.in_progress ? 'Continuar' : 'Iniciar');

/**
 * «Actualizado hace 2 min», by the phone's own clock, or `null` before the
 * tray ever came.
 *
 * @param {number|null} receivedAt
 * @param {number|null} now - The phone's time.
 */
export const updatedAgoText = (receivedAt, now) => {
  if (receivedAt == null || now == null) return null;
  const elapsed = now - receivedAt;
  return elapsed < MINUTE ? 'Actualizado recién' : `Actualizado hace ${spanText(elapsed)}`;
};
